import React, { useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { parseYmdToLocalDate, todayYmd } from '../../availability';
import { ProductFilter } from './ProductFilters';
import { FieldError } from '../FieldError';

const DATE_FILTER_ID = 'dates';

export interface DateFilterProps {
  startDate: string;
  endDate: string;
  rangeError?: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}

function formatDateLabel(ymd: string, lang: string): string {
  const date = parseYmdToLocalDate(ymd);
  if (!date) return ymd;
  return date.toLocaleDateString(lang === 'cs' ? 'cs-CZ' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export const DateFilter: React.FC<DateFilterProps> = ({
  startDate,
  endDate,
  rangeError = '',
  onStartDateChange,
  onEndDateChange,
}) => {
  const { t, lang } = useTranslation();
  const today = todayYmd();

  const summary = useMemo(() => {
    if (startDate && endDate && !rangeError) {
      return t('products.date_summary_range', {
        start: formatDateLabel(startDate, lang),
        end: formatDateLabel(endDate, lang),
      });
    }
    return t('products.date_summary_any');
  }, [startDate, endDate, rangeError, t, lang]);

  return (
    <ProductFilter id={DATE_FILTER_ID} summary={summary}>
      <div className="date-filter">
        <span className="date-filter__label">{t('products.date_filter')}</span>
        <div className="date-filter__fields">
          <label className="date-filter__field">
            <span>{t('rental.start_date')}</span>
            <input
              type="date"
              value={startDate}
              min={today}
              onChange={e => onStartDateChange(e.target.value)}
            />
          </label>
          <label className="date-filter__field">
            <span>{t('rental.end_date')}</span>
            <input
              type="date"
              value={endDate}
              min={startDate || today}
              onChange={e => onEndDateChange(e.target.value)}
            />
          </label>
        </div>
        <FieldError message={rangeError} />
      </div>
    </ProductFilter>
  );
};
